// components/TimelineModal.jsx
import React, { useMemo, useState, useRef } from 'react';
import styles from './styles/TimelineModal.module.css';

const STATUS_COLOR = {
  Закрыта: '#28a745',
  'Не доставлено': '#dc3545',
  Открыта: '#007bff',
};

export default function TimelineModal({
  routes,           // [{ routeNumber, receipts }]
  receiptRoute,     // { route_number, receipts }
  includeProducts,
  onClose,
  toggleIncludeProducts,
}) {
  // Преобразуем входные данные
  const routesToShow = useMemo(() => {
    if (Array.isArray(routes)) return routes;
    if (receiptRoute) {
      return [{
        routeNumber: receiptRoute.route_number,
        receipts: receiptRoute.receipts
      }];
    }
    return [];
  }, [routes, receiptRoute]);

  // Диапазон часов
  const [windowStart, setWindowStart] = useState(0);
  const [windowEnd, setWindowEnd] = useState(24);
  const wrapperRef = useRef(null);

  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragStartWindow = useRef({ start: 0, end: 24 });


  const resetWindow = () => {
    setWindowStart(0);
    setWindowEnd(24);
  };

  // Зум колесиком
  const onWheel = e => {
    e.preventDefault();
    if (!wrapperRef.current) return;
    const { left, width } = wrapperRef.current.getBoundingClientRect();
    let frac = (e.clientX - left) / width;
    frac = Math.max(0, Math.min(1, frac));
    const span = windowEnd - windowStart;
    const zoom = 1 + Math.sign(e.deltaY) * 0.1;
    const newSpan = Math.max(0.5, Math.min(24, span * zoom));
    const center = windowStart + frac * span;
    let start = center - frac * newSpan;
    let end = start + newSpan;
    if (start < 0) { start = 0; end = newSpan; }
    if (end > 24)  { end = 24; start = 24 - newSpan; }
    setWindowStart(start);
    setWindowEnd(end);
  };

  // Подготовка событий
  const eventsByRoute = useMemo(() => {
    return routesToShow.map(({ routeNumber, receipts }) => {
      const ev = [];
      receipts.forEach(r => {
        const push = (date, text, status, delivery_address) => {
          const h = date.getHours() + date.getMinutes() / 60;
          if (h >= windowStart && h <= windowEnd) {
            ev.push({ hour: h, date, text, status, delivery_address });
          }
        };

        if (includeProducts && Array.isArray(r.products)) {
          const ups = r.products
            .map(p => {
              const u = p.updatedAt ? new Date(p.updatedAt).getTime() : 0;
              const c = p.createdAt ? new Date(p.createdAt).getTime() : 0;
              return u > c ? u : null;
            })
            .filter(ts => ts);

          if (ups.length) {
            push(new Date(Math.max(...ups)), `№${r.receipt_number}: товары`, r.status, r.delivery_address);
          } else if (r.status === 'Открыта') {
            push(new Date(r.status_changed_at), `№${r.receipt_number}: нет данных`, r.status, r.delivery_address);
          }
        } else if (r.status_changed_at) {
          push(new Date(r.status_changed_at), `№${r.receipt_number}`, r.status, r.delivery_address);
        }
      });

      ev.sort((a, b) => a.hour - b.hour);
      // Добавляем дельты
      for (let i = 1; i < ev.length; i++) {
        const diff = ev[i].hour - ev[i - 1].hour;
        const dh = Math.floor(diff),
              dm = Math.round((diff - dh) * 60);
        ev[i].delta = `${dh}h ${dm}m`;
      }
      return { routeNumber, events: ev };
    });
  }, [routesToShow, includeProducts, windowStart, windowEnd]);

  // Подписи времени
  const span = windowEnd - windowStart;
  const tickMin = span > 12 ? 60 : span > 6 ? 30 : span > 3 ? 15 : span > 1 ? 5 : 1;
  const ticks = useMemo(() => {
    const arr = [];
    const startM = Math.ceil(windowStart * 60 / tickMin) * tickMin;
    const endM   = Math.floor(windowEnd * 60 / tickMin) * tickMin;
    for (let m = startM; m <= endM; m += tickMin) {
      const h = Math.floor(m / 60), mm = m % 60;
      const frac = (m / 60 - windowStart) / span;
      arr.push({
        label: `${String(h).padStart(2,'0')}:${String(mm).padStart(2,'0')}`,
        frac
      });
    }
    return arr;
  }, [windowStart, windowEnd, tickMin, span]);

  const onMouseDown = e => {
    // разрешаем только если диапазон меньше 24 (зум есть)
    if ((windowEnd - windowStart) >= 24) return;

    setIsDragging(true);
    dragStartX.current = e.clientX;
    dragStartWindow.current = { start: windowStart, end: windowEnd };
  };

  const onMouseMove = e => {
    if (!isDragging || !wrapperRef.current) return;
    const { width } = wrapperRef.current.getBoundingClientRect();
    const dx = e.clientX - dragStartX.current;
    const span = dragStartWindow.current.end - dragStartWindow.current.start;
    const deltaHours = (dx / width) * span;

    let newStart = dragStartWindow.current.start - deltaHours;
    let newEnd = dragStartWindow.current.end - deltaHours;

    // ограничиваем диапазон 0..24
    if (newStart < 0) {
      newEnd -= newStart;
      newStart = 0;
    }
    if (newEnd > 24) {
      const diff = newEnd - 24;
      newStart -= diff;
      newEnd = 24;
    }

    setWindowStart(newStart);
    setWindowEnd(newEnd);
  };

  const onMouseUp = () => {
    setIsDragging(false);
  };


  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalLarge}>
        <header className={styles.modalHeader}>
          <h2>Timeline ({windowStart.toFixed(1)}–{windowEnd.toFixed(1)} ч)</h2>
          <div>
            <button className={styles.smallBtn} onClick={resetWindow}>Сброс</button>
            <button className={styles.closeButton} onClick={onClose}>×</button>
          </div>
        </header>

        <div className={styles.modalBody}>
          <p className={styles.hint}>Зум: колесо мыши внутри шкалы</p>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={includeProducts}
              onChange={e => toggleIncludeProducts(e.target.checked)}
            />
            По времени сканирования товаров
          </label>

          <div className={styles.timelineContainer}>
            {/* Левая колонка с подписями маршрутов */}
            <div className={styles.leftColumn}>
              {eventsByRoute.map(({ routeNumber }) => (
                <div key={routeNumber} className={styles.routeLabelFixed}>
                  Маршрут №{routeNumber}
                </div>
              ))}
            </div>

            {/* Правая часть: сетка + скролл точек */}
            <div
              className={styles.timelineWrapper}
              onWheel={onWheel}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
              ref={wrapperRef}
            >

              <div className={styles.gridLayer}>
                {ticks.map((t,i) => (
                  <React.Fragment key={i}>
                    <div
                      className={styles.gridLineFull}
                      style={{ left: `${t.frac * 100}%` }}
                    />
                    <div
                      className={styles.tickHeader}
                      style={{ left: `${t.frac * 100}%` }}
                    >
                      {t.label}
                    </div>
                  </React.Fragment>
                ))}
              </div>

              <div className={styles.routesLayer}>
                {eventsByRoute.map(({ routeNumber, events }) => (
                  <div key={routeNumber} className={styles.routeRow}>
                    <div className={styles.routeEvents}>
                      {events.map((e,i) => {
                        const leftPct = ((e.hour - windowStart) / span) * 100;
                        return (
                          <div
                            key={i}
                            className={styles.marker}
                            style={{
                              left: `${leftPct}%`,
                              backgroundColor: STATUS_COLOR[e.status]
                            }}
                          >
                            <div className={styles.tooltip}>
                              <strong>{e.text}</strong><br/>
                              {e.date.toLocaleTimeString('ru-RU')}<br/>
                              {e.delta && `Δ ${e.delta}`}<br/>
                              Статус: {e.status}<br/>
                              {e.delivery_address && <>Адрес: {e.delivery_address}</>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
